import React, { createContext, useContext, useState, useEffect } from 'react';
import type { SpoilerLevel } from '@/types/wiki';

export type UserSpoilerPreference = 'none' | 'minor' | 'all';

interface SpoilerContextValue {
  preference: UserSpoilerPreference;
  setPreference: (pref: UserSpoilerPreference) => void;
  revealedItemIds: Set<string>;
  revealItem: (id: string) => void;
  hideItem: (id: string) => void;
  toggleItem: (id: string) => void;
  shouldProtect: (level: SpoilerLevel, id?: string) => boolean;
}

const SpoilerContext = createContext<SpoilerContextValue | null>(null);

const STORAGE_KEY = 'dd_wiki_spoiler_preference';

export const SpoilerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<UserSpoilerPreference>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'minor' || saved === 'all') return saved;
      return 'none'; // Default: protect all spoilers
    } catch {
      return 'none';
    }
  });

  const [revealedItemIds, setRevealedItemIds] = useState<Set<string>>(new Set());

  const setPreference = (pref: UserSpoilerPreference) => {
    setPreferenceState(pref);
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {}
  };

  const revealItem = (id: string) => {
    setRevealedItemIds(prev => new Set(prev).add(id));
  };

  const hideItem = (id: string) => {
    setRevealedItemIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleItem = (id: string) => {
    if (revealedItemIds.has(id)) hideItem(id);
    else revealItem(id);
  };

  const shouldProtect = (level: SpoilerLevel, id?: string): boolean => {
    if (level === 'spoiler-free') return false;
    if (id && revealedItemIds.has(id)) return false;

    if (preference === 'all') return false;
    if (preference === 'minor' && level === 'minor') return false;

    return true;
  };

  return (
    <SpoilerContext.Provider
      value={{
        preference,
        setPreference,
        revealedItemIds,
        revealItem,
        hideItem,
        toggleItem,
        shouldProtect
      }}
    >
      {children}
    </SpoilerContext.Provider>
  );
};

export function useSpoilers(): SpoilerContextValue {
  const ctx = useContext(SpoilerContext);
  if (!ctx) {
    throw new Error('useSpoilers must be used within a SpoilerProvider');
  }
  return ctx;
}
